import { Injectable } from '@angular/core';
import { LS_KEYS, ModuleType, Role, SessionState, UserInfo } from '@contracts';
import { BehaviorSubject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class SessionStore {
  private role$ = new BehaviorSubject<Role>('User');

  private language$ = new BehaviorSubject<string>(
    localStorage.getItem(LS_KEYS.language) ?? 'th',
  );

  constructor() {
    const role = this.getUserInfo()?.role ?? 'User';
    this.role$.next(role);
  }

  getLanguage(): string {
    return this.language$.value;
  }

  getLanguage$() {
    return this.language$.asObservable();
  }

  setLanguage(lang: string): void {
    localStorage.setItem(LS_KEYS.language, lang);
    this.language$.next(lang);
  }

  getAccessToken(): string | null {
    return localStorage.getItem(LS_KEYS.accessToken);
  }

  setAccessToken(token: string | null): void {
    if (!token) localStorage.removeItem(LS_KEYS.accessToken);
    else localStorage.setItem(LS_KEYS.accessToken, token);
  }

  getModule(): ModuleType | null {
    const v = localStorage.getItem(LS_KEYS.module);
    return v;
  }

  getDictionariesValidate() {
    const v = localStorage.getItem(LS_KEYS.i18.value);
    if (!v) return null;

    const parsed = JSON.parse(v);
    return parsed?.dictionaries_validate;
  }

  getFavoriteModules(): string[] {
    const v = localStorage.getItem(LS_KEYS.favoriteModules);
    try {
      return v ? JSON.parse(v) : [];
    } catch {
      return [];
    }
  }

  addFavorite(moduleLabel: string): void {
    const favs = this.getFavoriteModules();
    if (!favs.includes(moduleLabel)) {
      favs.push(moduleLabel);
      localStorage.setItem(LS_KEYS.favoriteModules, JSON.stringify(favs));
    }
  }

  removeFavorite(moduleLabel: string): void {
    const favs = this.getFavoriteModules().filter(
      (name) => name !== moduleLabel,
    );
    localStorage.setItem(LS_KEYS.favoriteModules, JSON.stringify(favs));
  }

  getUserId(): number | undefined {
    const u = this.getUserInfo();
    return u?.id;
  }

  getRole$() {
    return this.role$.asObservable();
  }

  getRole(): Role {
    const u = this.getUserInfo();
    return u?.role ?? 'User';
  }

  getAvatar() {
    const u = this.getUserInfo();
    return u?.avatar || null;
  }

  setModule(module: ModuleType | null): void {
    if (!module) localStorage.removeItem(LS_KEYS.module);
    else localStorage.setItem(LS_KEYS.module, module);
  }

  getUserInfo(): UserInfo | null {
    const raw = localStorage.getItem(LS_KEYS.userInfo);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as UserInfo;
    } catch {
      return null;
    }
  }

  setUserInfo(user: UserInfo | null): void {
    if (!user) {
      localStorage.removeItem(LS_KEYS.userInfo);
      this.role$.next('User');
    } else {
      localStorage.setItem(LS_KEYS.userInfo, JSON.stringify(user));
      this.role$.next(user.role);
    }
  }

  clear(): void {
    localStorage.clear();
  }

  snapshot(): SessionState {
    return {
      accessToken: this.getAccessToken(),
      module: this.getModule(),
      userInfo: this.getUserInfo(),
    };
  }

  isLoggedIn(): boolean {
    return !!this.getAccessToken();
  }
}
